import * as _ from 'underscore';

/**
 * A replacement value of a specific type, which could be a string or that type
 */
export type ReplacementValue<T> = string | T;

/**
 * A replacement function which returns a replacement value of a type
 */
export type ReplacementFunction<T> = (index: number) => ReplacementValue<T>;
/**
 * Interface to have string values mapped to any replacement value.
 * The replacement could be a static value, or a function which receives the index of the replacement. This is used
 * to give unique index keys to generated replacements
 */
export interface IReplacements<T> {
  [token: string]: ReplacementValue<T> | ReplacementFunction<T>;
}

/**
 * A function to replace tokens in square brackets by the result of corresponding functions. The result
 * is an array containing the chunked original string, and the replacement values.
 *
 * For example, using `{token1: () => 'value1'}` and the base string `'test with [token1]'`, the result
 * will be the array `['test with ', 'value1']`.
 *
 * @param baseString - A string containing tokens surrounded by square brackets
 * @param replacements - a {@link IReplacements} instance where the keys are the tokens, and the values are functions that
 *                       return replacement values
 */
export function interpolateString<T>(baseString: string, replacements: IReplacements<T>): ReplacementValue<T>[] {
  const result = [];

  // generate replacements and order them
  const replacementsInOrder = _.chain(Object.keys(replacements))
    .map(k => findInString(baseString, k)) // find occurrences and flatten them
    .flatten()
    .filter(v => v.start >= 0) // filter out patterns not present in the string
    .sortBy('start') // sort by start index
    .map((v, index) => replace(v, replacements, index)) // map found values to their replacement, passing their index
    .value();

  // iterate through replacements, chunk the base string and add it to the result
  let subStringIndex = 0;
  replacementsInOrder.forEach(rep => {
    if(rep.start !== subStringIndex) {
      const subString = baseString.substring(subStringIndex, rep.start);
      const wrapped = subString;
      result.push(wrapped);
    }

    result.push(rep.value);
    subStringIndex = rep.end + 1;
  });

  // if there is a trailing chunk, add it as well
  if(subStringIndex < baseString.length) {
    const lastChunk = baseString.substring(subStringIndex);
    const wrapped = lastChunk;
    result.push(wrapped);
  }

  return result;
}

/**
 * An interface to represent a found value in the string
 */
interface IFoundValues<T> {
  start: number;
  end: number;
  key: string;
  value?: ReplacementValue<T>;
}

/**
 * Find occurrences of a key in the base string.
 * @param baseString - The string to search
 * @param key - the key to replace. Only occurrences surrounded by square brackets are considered
 */
function findInString<T>(baseString: string, key: string): IFoundValues<T>[] {
  let searchStart = -1;
  const result: IFoundValues<T>[] = [];

  const keyWithBrackets = `[${key}]`;
  let index = -1;

  do {
    index = baseString.indexOf(keyWithBrackets, searchStart);
    const val = {
      start: index,
      end: index + keyWithBrackets.length - 1,
      key: key
    };

    result.push(val);
    searchStart = val.end;
  } while(index > -1);

  return result;
}

/**
 * replace a found value with the corresponding function, passing the index into that function
 * @param foundValue - a value with a key, start, and end indices.
 * @param replacements - a {@link IReplacements} instance containing replacement functions / values
 * @param index - the index of the found value
 */
function replace<T>(foundValue: IFoundValues<T>, replacements: IReplacements<T>, index: number): IFoundValues<T> {
  const rep = replacements[foundValue.key];

  foundValue.value = typeof rep === 'function' ? (rep as ReplacementFunction<T>)(index) : rep;
  return foundValue;
}
